import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { LanguageProvider } from './context/LanguageContext';
import { UserSession } from './types';
import { Navbar } from './components/Navbar';
import { AuthPage } from './pages/AuthPage';
import { LandingPage } from './pages/LandingPage';
import { CatalogPage } from './pages/CatalogPage';
import { CourseLearningPage } from './pages/CourseLearningPage';
import { ProfilePage } from './pages/ProfilePage';
import { B2BDashboardPage } from './pages/B2BDashboardPage';
import { VerifyBadgePage } from './pages/VerifyBadgePage';
import './styles/index.css';

const SESSION_KEY = 'akretrix_session';

// ── Inner app that has access to routing context ──────────────────────────────
const AppInner: React.FC<{
  session: UserSession | null;
  theme: 'dark' | 'light';
  setTheme: (t: 'dark' | 'light') => void;
  onLoginSuccess: (s: UserSession) => void;
  onLogout: () => void;
}> = ({ session, theme, setTheme, onLoginSuccess, onLogout }) => {
  const navigate = useNavigate();

  const handleGoToAuth = (tab?: 'signin' | 'signup') => {
    navigate(tab === 'signup' ? '/auth?tab=signup' : '/auth');
  };

  return (
    <div data-theme={theme} style={{ minHeight: '100vh', background: 'var(--bg-primary)' }}>
      {/* Navbar is visible only to authenticated users */}
      {session && (
        <Navbar
          session={session}
          theme={theme}
          setTheme={setTheme}
          onLogout={onLogout}
        />
      )}

      {!session ? (
        <Routes>
          {/* Public landing page — shown to visitors */}
          <Route path="/" element={<LandingPage onGoToAuth={handleGoToAuth} theme={theme} setTheme={setTheme} />} />

          {/* Dedicated auth route */}
          <Route path="/auth" element={<AuthPage onLoginSuccess={onLoginSuccess} />} />

          {/* Public badge verification — no login required */}
          <Route path="/verify" element={<VerifyBadgePage />} />
          <Route path="/verify/:badgeId" element={<VerifyBadgePage />} />

          {/* Catch-all redirects back to landing */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      ) : (
        <Routes>
          {/* Course Catalog */}
          <Route path="/" element={<CatalogPage session={session} />} />

          {/* Dedicated Course Learning Page */}
          <Route path="/course/:courseId" element={<CourseLearningPage session={session} />} />

          {/* Profile / Gamification Hub */}
          <Route path="/profile" element={<ProfilePage session={session} />} />

          {/* B2B Admin Dashboard */}
          <Route
            path="/dashboard"
            element={
              session.role === 'B2B_ADMIN'
                ? <B2BDashboardPage session={session} />
                : <Navigate to="/" replace />
            }
          />

          {/* Badge Verification */}
          <Route path="/verify" element={<VerifyBadgePage />} />
          <Route path="/verify/:badgeId" element={<VerifyBadgePage />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </div>
  );
};

// ── Root App ─────────────────────────────────────────────────────────────────
export const App: React.FC = () => {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  // Persist session across browser refreshes
  const [session, setSession] = useState<UserSession | null>(() => {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });

  const handleLoginSuccess = (userSession: UserSession) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(userSession));
    setSession(userSession);
  };

  const handleLogout = () => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
  };

  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppInner
          session={session}
          theme={theme}
          setTheme={setTheme}
          onLoginSuccess={handleLoginSuccess}
          onLogout={handleLogout}
        />
      </BrowserRouter>
    </LanguageProvider>
  );
};
